// Écritures applicables sans réseau.
//
// Chaque route décrit ce qu'une écriture change dans le modèle de lecture local
// (pour que les écrans se mettent à jour tout de suite, comme en ligne) et la
// réponse que l'écran recevra. La requête elle-même est mise en file et rejouée
// telle quelle au retour du réseau: le serveur reste seul juge des règles.
//
// Hors de cette table (encaissements, règlements, validation et émission d'une
// facture, ouvertures de session…), une écriture attend le serveur: l'argent ne
// passe jamais par une mise en file.
import { nouvelIdentifiant } from './paths';
import { appliquerEffetsLocaux } from './derived/effetsLocaux';
import { appliquerEvenementManquant } from './derived/dossiersManquants';
import { identiteLocale } from './droits';

const maintenant = () => new Date().toISOString();

const utilisateurLocal = () => {
  try {
    return JSON.parse(localStorage.getItem('app_v1_user') || localStorage.getItem('user') || 'null');
  } catch (erreur) {
    return null;
  }
};

const enveloppe = (forme, entite) => (forme === 'data' ? { success: true, data: entite } : entite);

/**
 * `envoyerId`: le serveur reconnaît l'identifiant choisi par l'appareil (vérifié
 * sur chaque route concernée). Sinon l'identifiant local reste interne et sa
 * correspondance avec celui du serveur est apprise au rejeu.
 */
const creation = (kind, { envoyerId = false, forme = 'raw', defauts = () => ({}), apres = null } = {}) => ({
  noyau: kind,
  envoyerId,
  async appliquer(ctx) {
    const id = ctx.corps?.id ? String(ctx.corps.id) : nouvelIdentifiant();
    const horodatage = maintenant();
    const entite = {
      ...defauts(ctx),
      ...ctx.corps,
      id,
      created_at: horodatage,
      updated_at: horodatage,
    };
    await ctx.ecrire(kind, id, entite);
    const touches = [{ kind, id, cree: true }];
    if (apres) touches.push(...((await apres(ctx, entite)) || []));
    const complete = (await ctx.lire(kind, id)) || entite;
    return { reponse: enveloppe(forme, complete), statut: forme === 'data' ? 201 : 200, touches };
  },
});

const modification = (kind, { forme = 'raw', transformer = null, apres = null } = {}) => ({
  noyau: kind,
  async appliquer(ctx) {
    const id = ctx.match[1];
    const existante = (await ctx.lire(kind, id)) || { id };
    const modifs = transformer ? transformer(ctx.corps || {}, existante, ctx) : ctx.corps || {};
    const entite = { ...existante, ...modifs, id: existante.id || id, updated_at: maintenant() };
    await ctx.ecrire(kind, id, entite);
    const touches = [{ kind, id }];
    if (apres) touches.push(...((await apres(ctx, entite, existante)) || []));
    const complete = (await ctx.lire(kind, id)) || entite;
    return { reponse: enveloppe(forme, complete), statut: 200, touches };
  },
});

const suppression = (kind, { apres = null } = {}) => ({
  async appliquer(ctx) {
    const id = ctx.match[1];
    const existante = await ctx.lire(kind, id);
    await ctx.retirer(kind, id);
    const touches = [{ kind, id, supprime: true }];
    if (apres) touches.push(...((await apres(ctx, existante)) || []));
    return { reponse: { success: true, data: { id } }, statut: 200, touches };
  },
});

const ID = '([^/]+)';
const ROUTES = [];
const route = (methode, motif, definition) => ROUTES.push({ methode, motif, ...definition });

// --- Parc: camions, chauffeurs, clients, transporteurs, convois -------------------------
[
  ['/trucks', { envoyerId: true }],
  ['/drivers', { envoyerId: true }],
  ['/clients', { envoyerId: true, defauts: () => ({ status: 'ACTIVE', current_balance: 0 }) }],
  ['/companies', { envoyerId: true }],
  ['/operations', { envoyerId: true, defauts: () => ({ active: true }) }],
].forEach(([kind, options]) => {
  route('post', new RegExp(`^${kind}$`), creation(kind, options));
  route('patch', new RegExp(`^${kind}/${ID}$`), modification(kind));
  route('put', new RegExp(`^${kind}/${ID}$`), modification(kind));
  route('delete', new RegExp(`^${kind}/${ID}$`), suppression(kind));
});

// --- Commandes ---------------------------------------------------------------------------
route('post', /^\/orders$/, creation('/orders', {
  envoyerId: true,
  defauts: (ctx) => ({
    status: ctx.corps?.status || 'DRAFT',
    order_date: ctx.corps?.order_date || maintenant(),
    delivered_quantity: 0,
    allocated_trips: [],
    allocated_trip_ids: [],
  }),
  apres: (ctx, commande) => appliquerEffetsLocaux('commande-creee', ctx, { commande }),
}));
route('patch', new RegExp(`^/orders/${ID}$`), modification('/orders', {
  apres: (ctx, commande) => appliquerEffetsLocaux('commande-modifiee', ctx, { commande }),
}));
route('delete', new RegExp(`^/orders/${ID}$`), suppression('/orders'));
route('patch', new RegExp(`^/orders/${ID}/confirm$`), modification('/orders', {
  transformer: () => ({ status: 'CONFIRMED' }),
  apres: (ctx, commande) => appliquerEffetsLocaux('commande-modifiee', ctx, { commande }),
}));
route('patch', new RegExp(`^/orders/${ID}/cancel$`), modification('/orders', {
  transformer: () => ({ status: 'CANCELLED' }),
  apres: (ctx, commande) => appliquerEffetsLocaux('commande-modifiee', ctx, { commande }),
}));
route('post', new RegExp(`^/orders/${ID}/allocate$`), {
  async appliquer(ctx) {
    const orderId = ctx.match[1];
    const tripId = ctx.parametres.trip_id || ctx.corps?.trip_id;
    const quantite = Number(ctx.parametres.quantity ?? ctx.corps?.quantity ?? 0);
    const touches = await appliquerEffetsLocaux('affectation', ctx, { orderId, tripId, quantite });
    return { reponse: { success: true, data: { order_id: orderId, trip_id: tripId, quantity: quantite } }, statut: 200, touches };
  },
});
route('post', new RegExp(`^/orders/${ID}/allocate-batch$`), {
  async appliquer(ctx) {
    const orderId = ctx.match[1];
    const lignes = ctx.corps?.allocations || ctx.corps?.items || [];
    const touches = [];
    for (const ligne of lignes) {
      touches.push(
        ...(await appliquerEffetsLocaux('affectation', ctx, {
          orderId,
          tripId: ligne.trip_id,
          quantite: Number(ligne.quantity ?? ligne.quantity_liters ?? 0),
        })),
      );
    }
    const appliquees = lignes
      .filter((ligne) => ligne.trip_id)
      .map((ligne) => ({
        order_id: orderId,
        trip_id: ligne.trip_id,
        quantity: Number(ligne.quantity ?? ligne.quantity_liters ?? 0),
      }));
    return {
      reponse: { success: true, data: { order_id: orderId, applied: appliquees, rejected: [], allocations: lignes } },
      statut: 200,
      touches,
    };
  },
});
route('post', new RegExp(`^/orders/${ID}/deliveries$`), {
  async appliquer(ctx) {
    const orderId = ctx.match[1];
    const touches = await appliquerEffetsLocaux('livraison', ctx, { orderId, livraison: ctx.corps || {} });
    return { reponse: { success: true, data: { order_id: orderId } }, statut: 200, touches };
  },
});

// --- Trajets -----------------------------------------------------------------------------
route('post', /^\/trips$/, creation('/trips', {
  envoyerId: true,
  defauts: () => ({ status: 'CREATED', expenses: [], road_expenses: 0 }),
  apres: (ctx, trajet) => appliquerEffetsLocaux('trajet-cree', ctx, { trajet }),
}));
route('patch', new RegExp(`^/trips/${ID}$`), modification('/trips', {
  apres: (ctx, trajet, avant) => appliquerEffetsLocaux('trajet-modifie', ctx, { trajet, avant }),
}));
route('put', new RegExp(`^/trips/${ID}$`), modification('/trips', {
  apres: (ctx, trajet, avant) => appliquerEffetsLocaux('trajet-modifie', ctx, { trajet, avant }),
}));
route('delete', new RegExp(`^/trips/${ID}$`), suppression('/trips', {
  apres: (ctx, trajet) => appliquerEffetsLocaux('trajet-supprime', ctx, { trajet }),
}));
route('patch', new RegExp(`^/trips/${ID}/status$`), {
  async appliquer(ctx) {
    const id = ctx.match[1];
    const statut = ctx.parametres.new_status;
    const touches = await appliquerEffetsLocaux('statut-trajet', ctx, {
      tripId: id,
      statut,
      note: ctx.parametres.note || null,
    });
    return { reponse: (await ctx.lire('/trips', id)) || { id, status: statut }, statut: 200, touches };
  },
});
route('post', new RegExp(`^/trips/${ID}/workflow/transition$`), {
  async appliquer(ctx) {
    const id = ctx.match[1];
    const touches = await appliquerEffetsLocaux('statut-trajet', ctx, {
      tripId: id,
      statut: ctx.corps?.to_step_code || null,
      codeAction: ctx.corps?.action_code || null,
      note: ctx.corps?.comment || null,
    });
    const trajet = await ctx.lire('/trips', id);
    return { reponse: { success: true, data: { trip_id: id, current_step_code: trajet?.status || null, status: trajet?.status || null } }, statut: 200, touches };
  },
});
route('post', new RegExp(`^/trips/${ID}/expenses$`), {
  async appliquer(ctx) {
    const id = ctx.match[1];
    const trajet = (await ctx.lire('/trips', id)) || { id };
    const frais = { id: nouvelIdentifiant(), ...ctx.corps, created_at: maintenant() };
    const entite = {
      ...trajet,
      expenses: [...(trajet.expenses || []), frais],
      road_expenses: Number(trajet.road_expenses || 0) + Number(ctx.corps?.amount || 0),
      updated_at: maintenant(),
    };
    await ctx.ecrire('/trips', id, entite);
    return { reponse: entite, statut: 200, touches: [{ kind: '/trips', id }] };
  },
});
route('delete', new RegExp(`^/trips/${ID}/expenses/${ID}$`), {
  async appliquer(ctx) {
    const [, id, fraisId] = ctx.match;
    const trajet = (await ctx.lire('/trips', id)) || { id };
    const retire = (trajet.expenses || []).find((f) => String(f.id) === String(fraisId));
    const entite = {
      ...trajet,
      expenses: (trajet.expenses || []).filter((f) => String(f.id) !== String(fraisId)),
      road_expenses: Math.max(Number(trajet.road_expenses || 0) - Number(retire?.amount || 0), 0),
      updated_at: maintenant(),
    };
    await ctx.ecrire('/trips', id, entite);
    return { reponse: entite, statut: 200, touches: [{ kind: '/trips', id }] };
  },
});

// --- Stock --------------------------------------------------------------------------------
route('post', /^\/stock$/, creation('/stock', {
  defauts: () => ({ movement_date: maintenant() }),
  apres: (ctx, mouvement) => appliquerEffetsLocaux('mouvement-stock', ctx, { mouvement }),
}));
route('patch', new RegExp(`^/stock/${ID}$`), modification('/stock', {
  apres: (ctx, mouvement) => appliquerEffetsLocaux('mouvement-stock', ctx, { mouvement }),
}));
route('delete', new RegExp(`^/stock/${ID}$`), suppression('/stock', {
  apres: (ctx) => appliquerEffetsLocaux('mouvement-stock', ctx, {}),
}));

// --- Frais, observations ------------------------------------------------------------------
route('post', /^\/observations$/, creation('/observations', { forme: 'data' }));
route('delete', new RegExp(`^/observations/${ID}$`), suppression('/observations'));
// Seuls les frais se saisissent sans réseau; un encaissement ou un règlement
// reste en ligne.
route('post', /^\/finance\/operations$/, {
  async appliquer(ctx) {
    if (!['EXPENSE', 'DRIVER_EXPENSE'].includes(String(ctx.corps?.kind || '').toUpperCase())) return null;
    const touches = await appliquerEffetsLocaux('frais-saisi', ctx, { corps: ctx.corps || {} });
    return {
      reponse: { success: true, data: { ...(ctx.corps || {}), id: ctx.corps?.id || nouvelIdentifiant() } },
      statut: 200,
      touches,
    };
  },
});

// --- Dossiers de manquants: constat, explication, contestation, décision --------------------
route('post', new RegExp(`^/shortages/trips/${ID}/events$`), {
  appliquer: (ctx) => appliquerEvenementManquant(ctx, { role: identiteLocale().role, utilisateur: utilisateurLocal() }),
});

// --- Entretien ----------------------------------------------------------------------------
const entretien = (kind, options, effet = null, cle = null) => {
  route('post', new RegExp(`^${kind}$`), creation(kind, {
    forme: 'data',
    ...options,
    apres: effet ? (ctx, entite) => appliquerEffetsLocaux(effet, ctx, { [cle]: entite }) : null,
  }));
  route('patch', new RegExp(`^${kind}/${ID}$`), modification(kind, {
    forme: 'data',
    apres: effet ? (ctx, entite) => appliquerEffetsLocaux(effet, ctx, { [cle]: entite }) : null,
  }));
};
entretien('/maintenance/work-orders', { defauts: () => ({ status: 'planned' }) });
entretien('/maintenance/breakdowns', { defauts: () => ({ status: 'open' }) });
entretien('/maintenance/immobilizations', { defauts: () => ({ status: 'active', started_at: maintenant() }) }, 'entretien-immobilisation', 'immobilisation');
entretien('/maintenance/readings', {}, 'entretien-releve', 'releve');
entretien('/maintenance/plans', {}, 'entretien-plan', 'plan');
entretien('/maintenance/documents', { defauts: () => ({ is_active: true }) }, 'entretien-papier', 'papier');
route('post', new RegExp(`^/maintenance/work-orders/${ID}/close$`), modification('/maintenance/work-orders', {
  forme: 'data',
  transformer: (corps) => ({ ...corps, status: 'done', completed_at: corps?.completed_at || maintenant() }),
}));
route('post', new RegExp(`^/maintenance/immobilizations/${ID}/release$`), modification('/maintenance/immobilizations', {
  forme: 'data',
  transformer: (corps) => ({ status: 'released', released_at: maintenant(), release_note: corps?.note || null }),
  apres: (ctx, entite) => appliquerEffetsLocaux('entretien-immobilisation', ctx, { immobilisation: entite }),
}));

// --- Factures: brouillon seulement (montants, statut, émission et règlements attendent le serveur) --
const creationBrouillon = creation('/invoices', {
  envoyerId: true,
  defauts: () => ({ status: 'DRAFT', invoice_number: null }),
});
route('post', /^\/invoices$/, {
  ...creationBrouillon,
  async appliquer(ctx) {
    // Une facture créée avec un autre statut est une émission: pas sans le serveur.
    const statut = String(ctx.corps?.status || 'DRAFT').toUpperCase();
    if (statut !== 'DRAFT') return null;
    return creationBrouillon.appliquer(ctx);
  },
});
route('patch', new RegExp(`^/invoices/${ID}$`), {
  async appliquer(ctx) {
    const id = ctx.match[1];
    const existante = await ctx.lire('/invoices', id);
    if (existante && String(existante.status || '').toUpperCase() !== 'DRAFT') return null;
    // Passer un brouillon à un autre statut est une émission.
    if (ctx.corps && 'status' in ctx.corps && String(ctx.corps.status || '').toUpperCase() !== 'DRAFT') return null;
    return modification('/invoices').appliquer(ctx);
  },
});

/** La route d'écriture qui correspond, avec son résultat de capture, ou null. */
export function trouverRouteEcriture(methode, chemin) {
  const m = String(methode || '').toLowerCase();
  for (const candidate of ROUTES) {
    if (candidate.methode !== m) continue;
    const match = candidate.motif.exec(chemin);
    if (match) return { route: candidate, match };
  }
  return null;
}
